import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { createShortcut } from '@/data/shortcuts';
import Toast from 'react-native-toast-message';

/**
 * Hook for silently saving a shortcut from a header menu / context action.
 * Dedupes by screen path, invalidates the shortcuts query, and shows a toast.
 *
 * Requires the logged-in user's id (pass loggedInUser?.id).
 */
const useSaveShortcut = (personID) => {
    const queryClient = useQueryClient();

    return useCallback(async ({ screen, label, icon = 'link-outline', library = 'ionicon' }) => {
        if (!personID) {
            Toast.show({ type: 'error', text2: 'Not available right now' });
            return;
        }

        const queryKey = QueryKeys.shortcuts.byPersonId(personID);
        const existing = queryClient.getQueryData(queryKey) || [];
        if (existing.some(s => s.screen === screen)) {
            Toast.show({ type: 'info', text2: 'Already in your shortcuts' });
            return;
        }

        try {
            const newShortcut = await createShortcut({ personID, screen, label, icon, library });
            queryClient.setQueryData(queryKey, (old) => old ? [...old, newShortcut] : [newShortcut]);
            Toast.show({ type: 'success', text2: 'Added to shortcuts' });
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to add shortcut' });
        }
    }, [personID, queryClient]);
};

export default useSaveShortcut;
